// src/routes/licensee-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
  FaStore,
  FaFileInvoiceDollar,
} from 'react-icons/fa';
import {
  FiCreditCard,
  FiDownload,
} from 'react-icons/fi';
import {
  BiPurchaseTag,
} from 'react-icons/bi';
import {
  HiDocumentSearch,
} from 'react-icons/hi';
import {
  RiContractFill,
  RiAlertFill,
} from 'react-icons/ri';
import { 
  FileText, 
  Receipt 
} from 'lucide-react';

// Existing lazy loads
const PublicWorks = lazy(() => import('@/pages/management/PublicWorks'));
const LicensePurchase = lazy(() => import('@/pages/management/LicensePurchase'));
const LicensePayments = lazy(() => import('@/pages/management/LicensePayments'));
const ViewContracts = lazy(() => import('@/pages/management/ViewContracts'));
const ManageContracts = lazy(() => import('@/pages/management/ManageContracts'));


// Licensee routes
export const licenseeRoutes: RoutesProps[] = [
  // Marketplace & Discovery
  {
    path: "/marketplace/works",
    element: <PublicWorks />,
    name: "Khám phá tác phẩm có bản quyền",
    roles: ["licensee", "guest"],
    icon: FaStore,
  },
  {
    path: "/public/works",
    element: <PublicWorks />,
    name: "Khám phá tác phẩm có bản quyền",
    roles: ["licensee", "guest"],
    icon: FaStore,
  },


  // Existing License Management
  {
    path: "/licenses/purchase",
    element: <LicensePurchase />,
    name: "Mua giấy phép sử dụng",
    roles: ["licensee"],
    icon: BiPurchaseTag,
  },
  {
    path: "/licenses/payments",
    element: <LicensePayments />,
    name: "Thanh toán phí bản quyền",
    roles: ["licensee"],
    icon: FiCreditCard,
  },

  // Contract Management
  {
    path: "/licenses/contracts",
    element: <ViewContracts />,
    name: "Xem hợp đồng hiện tại",
    roles: ["licensee"],
    icon: HiDocumentSearch,
  },
  {
    path: "/licenses/contracts/manage",
    element: <ManageContracts />,
    name: "Gia hạn/Hủy hợp đồng",
    roles: ["licensee"],
    icon: RiContractFill,
  }
];

// ✅ UPDATED: Navigation structure for licensee features
export const licenseeNavigationStructure = {
  marketplace: {
    key: 'marketplace',
    translationKey: 'marketplace',
    icon: FaStore,
    roles: ['licensee', 'guest'],
    children: [
      {
        key: 'public-works',
        translationKey: 'publicWorks',
        path: '/public/works',
        icon: FaStore,
        roles: ['licensee', 'guest'],
      },
      {
        key: 'marketplace-works',
        translationKey: 'marketplaceWorks',
        path: '/marketplace/works',
        icon: FaStore,
        roles: ['licensee', 'guest'],
      },
    ],
  },
  
  // ✅ NEW: My Licenses Section
  myLicenses: {
    key: 'my-licenses',
    translationKey: 'myLicenses',
    icon: FileText,
    roles: ['licensee', 'right_owner'],
    children: [
      {
        key: 'purchased-licenses',
        translationKey: 'purchasedLicenses',
        path: '/licenses',
        icon: FileText,
        roles: ['licensee', 'right_owner'],
      },
      {
        key: 'invoices',
        translationKey: 'invoices',
        path: '/invoices',
        icon: Receipt,
        roles: ['licensee', 'right_owner'],
      },
    ],
  },
  
  licenseManagement: {
    key: 'license-management',
    translationKey: 'licenseManagement',
    icon: BiPurchaseTag,
    roles: ['licensee'],
    children: [
      {
        key: 'purchase-license',
        translationKey: 'purchaseLicense',
        path: '/licenses/purchase',
        icon: BiPurchaseTag,
        roles: ['licensee'],
      },
      {
        key: 'license-payments',
        translationKey: 'licensePayments',
        path: '/licenses/payments',
        icon: FiCreditCard,
        roles: ['licensee'],
      },
    ],
  },
  
  contractManagement: {
    key: 'contract-management',
    translationKey: 'contractManagement',
    icon: HiDocumentSearch,
    roles: ['licensee'],
    children: [
      {
        key: 'view-contracts',
        translationKey: 'viewContracts',
        path: '/licenses/contracts',
        icon: HiDocumentSearch,
        roles: ['licensee'],
      },
      {
        key: 'manage-contracts',
        translationKey: 'manageContracts',
        path: '/licenses/contracts/manage',
        icon: RiContractFill,
        roles: ['licensee'],
      },
    ],
  },
  
  // ✅ NEW: Support Section
  support: {
    key: 'support',
    translationKey: 'support',
    icon: RiAlertFill,
    roles: ['licensee', 'right_owner'],
    children: [
      {
        key: 'create-dispute',
        translationKey: 'createDispute',
        path: '/disputes/create',
        icon: RiAlertFill,
        roles: ['licensee', 'right_owner'],
      },
    ],
  },
};

// Helper functions
export const getLicenseeRoutesForRole = (userRoles: string[]): RoutesProps[] => {
  return licenseeRoutes.filter((route) => {
    if (!route.roles || route.roles.length === 0) return true;
    return route.roles.some((role) => userRoles.includes(role));
  });
};

export const hasAccessToLicenseeFeatures = (userRoles: string[]): boolean => {
  return userRoles.includes('licensee') || userRoles.includes('guest') || userRoles.includes('right_owner');
};

// ✅ NEW: Get navigation items (filter out hideInNav)
export const getLicenseeNavigationItems = (userRoles: string[]): RoutesProps[] => {
  return licenseeRoutes.filter((route) => {
    // Filter by role
    if (route.roles && !route.roles.some((role) => userRoles.includes(role))) {
      return false;
    }
    // Filter out hidden items
    return !route.hideInNav;
  });
};

export default licenseeRoutes;
